import React from "react";
import { useNavigate } from "react-router-dom";
import { Chip } from "./Chip";

/**
 * Card tone -> Chip tone. `warning` borrows the amber "internal" chip: the
 * palette has no warning token of its own, and that is its amber.
 */
const CHIP_TONE = {
  success: "success",
  info: "info",
  warning: "internal",
  danger: "danger",
  neutral: "neutral",
};

/**
 * Full literal class strings — `tailwind.config.js` purges anything assembled
 * by concatenation (see the note in PlanShareCard).
 */
const BUTTON_CLASS = {
  primary:
    "bg-orange-primary text-white hover:bg-dark-orange focus-visible:ring-orange-primary",
  secondary:
    "border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-400",
  danger:
    "border border-danger bg-white text-danger-dark hover:bg-danger-bg focus-visible:ring-danger",
};

const formatTime = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

/**
 * A system event card: something that happened in another service, posted into
 * the room — a call booked, moved, cancelled, about to start.
 *
 * Centred like the system pill it replaces, because nobody in the room wrote
 * it. Rendered entirely from the normalised card; nothing here knows what a
 * booking is.
 *
 * A superseded card — one a newer card for the same thing has replaced — is
 * dimmed and loses its buttons, so the only "View booking" a coach can press
 * belongs to the card that is still true.
 *
 * @param {Object} props
 * @param {import('../../utils/commsEventCard').EventCard} props.card
 * @param {string} [props.createdAt] - The message's timestamp.
 * @param {boolean} [props.superseded] - Also dim when the thread has seen a
 *   newer card for the same entity (the server stamps it a moment later).
 */
export const EventCard = ({ card, createdAt, superseded = false }) => {
  const navigate = useNavigate();
  const stale = superseded || card.superseded;
  const time = createdAt ? formatTime(createdAt) : "";

  return (
    <article
      data-testid="event-card"
      data-superseded={stale ? "true" : "false"}
      aria-label={card.status ? `${card.title}, ${card.status.label}` : card.title}
      className={`w-full max-w-[340px] rounded-xl border border-gray-200 bg-white px-4 py-3 text-left text-sm ${
        stale ? "opacity-60" : "shadow-sm"
      }`}
    >
      <header className="flex items-start justify-between gap-3">
        <h4 className="min-w-0 break-words font-semibold text-gray-900">{card.title}</h4>
        {card.status && (
          <Chip tone={CHIP_TONE[card.status.tone]} className="flex-shrink-0 !px-2 !py-0.5">
            {card.status.label}
          </Chip>
        )}
      </header>

      {card.text && (
        <p className="mt-1 whitespace-pre-wrap break-words text-gray-700">{card.text}</p>
      )}

      {card.fields.length > 0 && (
        <dl className="mt-2 space-y-1 border-t border-gray-100 pt-2 text-xs">
          {card.fields.map((field) => (
            <div key={field.label} className="flex gap-2">
              <dt className="w-16 flex-shrink-0 text-gray-500">{field.label}</dt>
              <dd className="min-w-0 break-words font-medium text-gray-800">{field.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {stale ? (
        <p className="mt-2 text-[11px] text-gray-500">Replaced by a newer update</p>
      ) : (
        card.actions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {card.actions.map((action) => (
              <button
                key={action.key}
                type="button"
                onClick={() => navigate(action.to)}
                className={`min-h-[36px] rounded-lg px-3 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
                  BUTTON_CLASS[action.style] || BUTTON_CLASS.secondary
                }`}
              >
                {action.label}
              </button>
            ))}
          </div>
        )
      )}

      {time && <p className="mt-2 text-right text-[11px] text-gray-400">{time}</p>}
    </article>
  );
};

export default EventCard;
